import { isPresent } from '@shared/utils';
import { debouncePromise } from '@shared/utils/promises';
import { get, isFunction, isObject, orderBy } from 'lodash-es';
import { attachSelectSearch } from '../index';

export type GenerateSelectedHtml = (option, label: string) => string;

export interface Config {
    [prop: string]: any;

    generateSelectedHtml?: GenerateSelectedHtml;
    groupParams?: {
        groupByColumn: string;
        groups: {
            name: string;
            value: any | any[];
        }[];
    };

    shouldDisableOption?: (option: any) => boolean;
}

export function createSelectDirective({
                                          $presenter = undefined,
                                          data = null,
                                          fetchCallback = undefined,
                                          fetcherName = undefined,
                                          fetcherParams = {},
                                          groupParams = undefined,
                                          optionTemplate = undefined,
                                          generateSelectedHtml = undefined,
                                          labelProperty = 'name',
                                          metaTemplate = undefined,
                                          multiple = false,
                                          orderByColumn = undefined,
                                          searchFunction = undefined,
                                          showNone = false,
                                          trackByProperty = 'id',
                                          valueProperty = undefined,
                                          emptyMessage = 'No Items',
                                          shouldDisableOption = undefined
                                      }: Config) {
    selectDirective.$inject = ['$errorHandler', '$util', fetcherName].filter(Boolean);

    if (metaTemplate) {
        metaTemplate = `<div class="app-form-select-option-meta">${metaTemplate}</div>`;
    }

    const defaultOptionTemplate = `
         <md-icon ng-if="option.id === '$$new'">
            add
        </md-icon>

        <div>
            <span md-highlight-text="ui.searchQuery"
                  data-testid="{{ ::get(option, labelProperty, get(option, 'label')) }}"
                md-highlight-flags="gi">

                 {{ ::get(option, labelProperty, get(option, 'label')) }}
             </span>

             ${metaTemplate || ''}
        </div>
    `;

    const noneTemplate = showNone
        ? `
        <md-option class="app-form-select-option"
                   ng-value="null">
            None
        </md-option>
    `
        : '';

    function selectDirective($errorHandler, $util, $fetcher) {
        return {
            //language=HTML
            template: `
                <md-input-container>
                    <label ng-if="label">{{ label }}</label>

                    <md-select name="{{ name }}"
                               ng-model="_value"
                               ng-model-options="{ trackBy: '$value.${trackByProperty}' }"
                               ng-change="_onChange()"
                               md-on-close="_onClose()"
                               md-on-open="_onOpen()"
                               ng-disabled="disabled || ui.fetching"
                               ng-required="required"
                               md-selected-html="_generateSelectedHtml()"
                               placeholder="{{ placeholder }}"
                               aria-label="{{ label }}"
                               ${multiple ? 'multiple="true"' : ''}>

                        <md-select-header class="app-form-select-search-container"
                                          ng-if="options.length > 5">
                            <div class="app-form-select-search-icon">
                                <md-icon>search</md-icon>
                            </div>

                            <input type="search"
                                   class="md-text"
                                   ng-model="ui.searchQuery"
                                   placeholder="Search...">
                        </md-select-header>

                        <md-select-header class="app-form-select-all"
                                          md-ink-ripple
                                          ng-if="multiple && value.length !== options.length"
                                          ng-click="selectAll()">
                            <md-icon>done_all</md-icon>
                            Select all
                        </md-select-header>

                        <md-select-header class="app-form-select-clear"
                                          md-ink-ripple
                                          ng-if="value.length"
                                          ng-click="clear()">
                            <md-icon>clear</md-icon>
                            Clear
                        </md-select-header>

                        ${noneTemplate}

                        <md-option class="app-form-select-option"
                                   ng-if="!shouldGroup"
                                   ng-repeat="option in options | filter:search track by option.${trackByProperty}"
                                   ng-disabled="ui.isOptionDisabled(option)"
                                   ng-value="option">
                            ${optionTemplate || defaultOptionTemplate}
                        </md-option>

                        <md-optgroup ng-if="shouldGroup"
                                     ng-repeat="group in availableGroups"
                                     label="{{ group.name }}">
                            <md-option class="app-form-select-option"
                                       ng-repeat="option in groupedOptions[group.name] | filter:search track by option.${trackByProperty}"
                                       ng-disabled="ui.isOptionDisabled(option)"
                                       ng-value="option">
                                ${optionTemplate || defaultOptionTemplate}
                            </md-option>
                        </md-optgroup>

                        <md-option ng-if="ui.fetching"
                                   disabled
                                   ng-value="null">
                            Loading...
                        </md-option>

                        <md-option ng-if="!ui.fetching && !options.length"
                                   disabled>
                            ${emptyMessage || 'No items'}
                        </md-option>
                    </md-select>

                    <app-loading small ng-if="ui.fetching"></app-loading>

                    <div class="hint" ng-if="hint">{{ hint }}</div>

                    <div ng-if="form && name" ng-messages="form[name].$error">
                        <div ng-message="required">Required</div>
                        <div ng-repeat="(key, message) in errorMessages" ng-message="{{ key }}">{{ message }}</div>
                    </div>

                    <ng-transclude></ng-transclude>
                </md-input-container>

                <md-button class="md-icon-button app-form-field-clear"
                           ng-if="isClearable"
                           ng-disabled="value === undefined"
                           ng-click="clear()">
                    <md-icon>clear</md-icon>
                </md-button>
            `,
            transclude: true,
            scope: {
                isClearable: '=?clearable',
                dispatchChange: '&?change',
                data: '=?data',
                disable: '=?disable',
                disabled: '=?ngDisabled',
                errorMessages: '=?errorMessages',
                form: '=form',
                hint: '@?hint',
                label: '@label',
                name: '@name',
                newOption: '@?newOption',
                noFloat: '=?noFloat',
                params: '=?params',
                placeholder: '@?placeholder',
                required: '=?required',
                teams: '=?teams',
                value: '=value',
                valueProperty: '@?valueProperty',
            },
            link: function (scope, element, attrs) {
                const _debouncedCallFetch = debouncePromise(_callFetch, 100);

                element.addClass('app-form-field');
                element.addClass('app-form-select');

                scope.multiple = multiple;

                if (!scope.valueProperty) {
                    scope.valueProperty = valueProperty;
                }

                scope.$watch('isClearable', value => {
                    if (value) {
                        element.addClass('app-form-select--clearable');
                    } else {
                        element.removeClass('app-form-select--clearable');
                    }
                });

                scope.$watch('hint', value => {
                    if (value) {
                        element.addClass('app-form-select--has-hint');
                    } else {
                        element.removeClass('app-form-select--has-hint');
                    }
                });

                let hasInteracted = false;

                // Properties
                const ui = (scope.ui = {
                    fetching: false,
                    searchQuery: '',

                    isOptionDisabled(option: any) {
                        if (shouldDisableOption) {
                            if (shouldDisableOption(option)) {
                                return true;
                            }
                        }

                        const id = option[trackByProperty];

                        if (scope.disable) {
                            if (Array.isArray(scope.disable)) {
                                return scope.disable.indexOf(id) > -1;
                            } else if (scope.disable === id) {
                                return true;
                            }
                        }

                        return false;
                    },
                });

                if (data) {
                    scope.data = data;
                }

                scope.labelProperty = labelProperty;
                scope.options = [];
                scope._value = null;

                // Methods

                scope._onChange = _onChange;
                scope._onOpen = () => (hasInteracted = true);
                scope._onClose = () => (hasInteracted = true);
                scope._generateSelectedHtml = _generateSelectedHtml;
                scope.search = searchFunction || search;
                scope.get = get;

                scope.selectAll = selectAll;
                scope.clear = clear;

                // Main

                if (scope.required === undefined && attrs.required !== undefined) {
                    scope.required = true;
                }

                function _refreshOptions(newValue, oldValue) {
                    if (!oldValue && !newValue) {
                        return;
                    }

                    if (!angular.isDefined(scope.data)) {
                        _fetchOptions();
                    }
                }

                scope.$watchCollection('teams', _refreshOptions);
                scope.$watchCollection('params', _refreshOptions);

                if (angular.isDefined(scope.data)) {
                    _setOptions(scope.data);
                } else {
                    _fetchOptions();
                }

                attachSelectSearch(element, scope);

                scope.$watch('_value', _value => {
                    if ($presenter && _value && _value.id !== '$$new') {
                        $presenter.attach(_value, false);
                    }

                    if (scope.valueProperty) {
                        if (multiple) {
                            if (scope.options && scope.options.length > 0) {
                                scope.value = Array.isArray(_value)
                                    ? _value
                                        .map(_v =>
                                            (_v && _v.hasOwnProperty(scope.valueProperty)) ? _v[scope.valueProperty] : null,
                                        )
                                        .filter(isPresent)
                                    : undefined;
                            }
                        } else if (isObject(_value)) {
                            scope.value = _value[scope.valueProperty];
                        }
                    } else {
                        scope.value = _value;
                    }
                });

                scope.$watchCollection('value', valueWatcher);
                scope.$watchCollection('options', optionsWatcher);

                scope.$watch('data', (data) => {
                    if (data) {
                        scope.options = data;
                    }
                    ;
                });

                function optionsWatcher() {
                    valueWatcher(scope.value);
                }

                valueWatcher(scope.value);

                // Functions

                function clear() {
                    hasInteracted = true;

                    scope.value = undefined;
                    valueWatcher(scope.value);

                    _onChange();
                }

                function selectAll() {
                    if (valueProperty) {
                        scope.value = scope.options.map(_ => _[valueProperty]);
                    } else {
                        scope.value = scope.options;
                    }
                }

                function valueWatcher(value) {
                    if (scope.valueProperty) {
                        if (multiple) {
                            if (value === undefined) {
                                scope._value = null;
                            } else if (Array.isArray(value)) {
                                if (scope.options && scope.options.length > 0) {
                                    const _value = value.map(v =>
                                        scope.options.find(option => option[scope.valueProperty] === v),
                                    );

                                    if (!angular.equals(scope._value, _value)) {
                                        scope._value = _value;
                                    }
                                }
                            }
                        } else {
                            // Find the value in the current options

                            scope._value = scope.options.find(option => option[scope.valueProperty] === value);
                        }
                    } else {
                        scope._value = value;
                    }
                }

                function _generateSelectedHtml() {
                    if (generateSelectedHtml) {
                        return generateSelectedHtml(scope._value, scope.label)
                            || scope.label
                            || scope.placeholder
                            || null;
                    }

                    if (scope._value && !(Array.isArray(scope._value) && !scope._value.length)) {
                        if (multiple) {
                            return (
                                scope._value
                                    .map(option => {
                                        return `<span class="badge">${get(option, labelProperty)}</span>`;
                                    })
                                    .join('') || scope.label
                            );
                        }

                        return get(scope._value, labelProperty, get(scope._value, 'label', scope.label)) || null;
                    }

                    return scope.label || scope.placeholder || null;
                }

                function _callFetch() {
                    const params: any = {
                        order_by: orderByColumn || labelProperty,
                        direction: 'asc',
                        ...fetcherParams,
                        ...(scope.params || {}),
                    };

                    if (Array.isArray(scope.teams)) {
                        params.team = scope.teams.map(t => t.id);
                    }

                    if (fetchCallback) {
                        return fetchCallback($fetcher, params);
                    }

                    return $fetcher.all(params);
                }

                function _fetchOptions() {
                    if (!$fetcher) {
                        return;
                    }

                    ui.fetching = true;

                    return _debouncedCallFetch()
                        .then($util.nextTick(data => _setOptions(data.data)))
                        .catch(error => $errorHandler.handle(error))
                        .finally($util.nextTick(() => (ui.fetching = false)));
                }

                function _onChange() {
                    if (!hasInteracted) {
                        return;
                    }

                    if (isFunction(scope.dispatchChange)) {
                        scope.dispatchChange({
                            value: scope.value,
                        });
                    }
                }

                function search(option) {
                    if (!ui.searchQuery) {
                        return true;
                    }

                    const queryParts = ui.searchQuery.toLowerCase().split(' ');

                    for (const queryPart of queryParts) {
                        if (option.id === '$$new') {
                            continue;
                        }
                        if (
                            get(option, labelProperty)
                                .toLowerCase()
                                .indexOf(queryPart) !== -1
                        ) {
                            return true;
                        }
                    }

                    return false;
                }

                function _setOptions(options) {
                    if ($presenter) {
                        if (Array.isArray(options)) {
                            options.forEach(o => {
                                if (o.id !== '$$new') {
                                    $presenter.attach(o);
                                }
                            });
                        }
                    }

                    if (scope.disable) {
                        options.forEach(option => {
                            option.$$disabled = ui.isOptionDisabled(option);
                        });
                    }

                    scope.options = orderBy(options, ['$$disabled', orderByColumn || labelProperty], ['asc', 'asc']);

                    scope.shouldGroup = !!groupParams;

                    if (scope.shouldGroup) {
                        _prepareGroupedOptions(scope.options);
                    }

                    if (scope.newOption) {
                        scope.options.unshift({
                            id: '$$new',
                            label: scope.newOption
                        });
                    }
                }

                function _prepareGroupedOptions(allOptions: any[]) {
                    const groupsList = groupParams.groups;
                    scope.groupByColumn = get(groupParams, 'groupByColumn');

                    scope.groupedOptions = [];
                    scope.availableGroups = [];

                    groupsList.forEach(group => {
                        const filteredOptions = allOptions.filter(option => {
                            if (Array.isArray(group.value)) {
                                return group.value.indexOf(option[scope.groupByColumn]) > -1;
                            } else {
                                return option[scope.groupByColumn] === group.value;
                            }
                        });

                        if (filteredOptions.length > 0) {
                            scope.availableGroups.push(group);
                            scope.groupedOptions[group.name] = filteredOptions;
                        }
                    });
                }
            },
        };
    }

    return selectDirective;
}
